import { useQuery } from "@tanstack/react-query";
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { useAuth } from "../../../auth/AuthProvider";
import { getHourlyImpacts } from "../../../api/impacts";
import { useRangeMode } from "../../impact/context/RangeModeContext";

/**
 * Generation Profile card.
 *
 * Generation is the single biggest driver of an LCA result, and it can
 * arrive many ways — directly metered, reported aggregates calibrated
 * onto a SAM shape, fully SAM-estimated, or gap-filled. This card makes
 * that provenance first-class: a daily generation shape plus the exact
 * per-source hour breakdown from the run's
 * summary_json.generation_metadata.profile_source_breakdown.
 */

interface GenerationMeta {
    actual_generation_gwh?: number;
    capacity_factor?: number;
    hours_modeled?: number;
    profile_source?: string;
    profile_source_breakdown?: Record<string, number>;
}

interface Props {
    projectId: string;
    generationMeta?: GenerationMeta | null;
    runStatus?: string | null;
}

// Bucket raw source tags into the four provenance classes + colors.
const SOURCE_CLASSES: Record<
    string,
    { label: string; color: string }
> = {
    metered: { label: "Metered", color: "#34d399" }, // quantum/green
    calibrated: { label: "Calibrated", color: "#38bdf8" }, // ecosystem/blue
    estimated: { label: "Estimated", color: "#fbbf24" }, // economic/amber
    gap_filled: { label: "Gap-filled", color: "#9ca3af" }, // muted
};

function classify(tag: string): keyof typeof SOURCE_CLASSES {
    const t = tag.toLowerCase();
    if (t.startsWith("sam_calibrated") || t.startsWith("sam_zero")) return "calibrated";
    if (t.startsWith("sam")) return "estimated";
    if (t.startsWith("gap_filled") || t === "interpolated") return "gap_filled";
    return "metered";
}

function ProvenanceBar({
    breakdown,
}: {
    breakdown: Record<string, number>;
}) {
    const agg: Record<string, number> = {};
    let total = 0;
    for (const [tag, n] of Object.entries(breakdown)) {
        const cls = classify(tag);
        agg[cls] = (agg[cls] || 0) + n;
        total += n;
    }
    if (total <= 0) return null;
    const order: (keyof typeof SOURCE_CLASSES)[] = [
        "metered",
        "calibrated",
        "estimated",
        "gap_filled",
    ];
    return (
        <div>
            <div className="flex h-3 w-full overflow-hidden rounded-full bg-surface-600">
                {order.map((k) =>
                    agg[k] ? (
                        <div
                            key={k}
                            style={{
                                width: `${(agg[k] / total) * 100}%`,
                                backgroundColor: SOURCE_CLASSES[k].color,
                            }}
                            title={`${SOURCE_CLASSES[k].label}: ${agg[k]} h (${(
                                (agg[k] / total) *
                                100
                            ).toFixed(1)}%)`}
                        />
                    ) : null,
                )}
            </div>
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1">
                {order.map((k) =>
                    agg[k] ? (
                        <span
                            key={k}
                            className="flex items-center gap-1.5 text-caption text-text-secondary"
                        >
                            <span
                                className="h-2 w-2 rounded-full"
                                style={{ backgroundColor: SOURCE_CLASSES[k].color }}
                            />
                            {SOURCE_CLASSES[k].label}
                            <span className="text-text-muted">
                                {((agg[k] / total) * 100).toFixed(0)}%
                            </span>
                        </span>
                    ) : null,
                )}
            </div>
        </div>
    );
}

function GenTooltip({ active, payload, label }: any) {
    if (!active || !payload?.length) return null;
    return (
        <div className="rounded-lg border border-border bg-surface-600 px-3 py-2 shadow-lg">
            <p className="text-caption text-text-secondary">{label}</p>
            <p className="text-body-sm font-semibold text-quantum-400">
                {Number(payload[0].value).toFixed(1)} MWh
            </p>
        </div>
    );
}

export function GenerationProfileCard({
    projectId,
    generationMeta,
    runStatus,
}: Props) {
    const { token } = useAuth();
    const { mode: rangeMode } = useRangeMode();

    // Derive the daily shape from hourly impacts, not the daily endpoint.
    // hourly_impacts is always persisted per run; the daily_project_impacts
    // continuous aggregate can lag (empty until the cagg materializes the
    // run's rows), which would leave the chart blank.
    const { data: daily, isLoading } = useQuery({
        queryKey: ["genProfileHourly", projectId],
        enabled: !!token && !!projectId && runStatus === "completed",
        staleTime: 1000 * 60 * 10,
        queryFn: async () => {
            const r = await getHourlyImpacts(token!, projectId, { limit: 20000 });
            const byDay: Record<string, number> = {};
            for (const h of r.data ?? []) {
                const day = (h.timestamp || "").slice(0, 10);
                if (!day) continue;
                byDay[day] = (byDay[day] || 0) + (h.generation_mwh || 0);
            }
            return Object.entries(byDay)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([date, mwh]) => ({ date, mwh }));
        },
    });

    if (runStatus !== "completed") return null;

    const breakdown = generationMeta?.profile_source_breakdown;
    const annualGwh = generationMeta?.actual_generation_gwh;
    const cf = generationMeta?.capacity_factor;
    const hours = generationMeta?.hours_modeled;

    const chartData = daily ?? [];

    return (
        <div className="rounded-xl border border-border bg-surface-700 p-6">
            <div className="mb-1 flex items-center justify-between">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-text-primary">
                    Generation Profile
                    <span
                        className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium text-text-muted bg-surface-600 border border-border"
                        title="Generation is the fixed physical driver of the result — it never gets a sensitivity band."
                    >
                        <span className="h-1 w-1 rounded-full bg-text-muted" /> fixed input
                    </span>
                </h3>
                {typeof annualGwh === "number" && (
                    <span className="text-body-sm text-text-secondary">
                        {annualGwh.toFixed(1)} GWh
                        {typeof cf === "number" && (
                            <span className="text-text-muted">
                                {" "}
                                · {(cf * 100).toFixed(1)}% CF
                            </span>
                        )}
                    </span>
                )}
            </div>
            <p className="mb-4 text-xs text-text-secondary">
                The biggest driver of this project's results — and how it was
                derived.
                {rangeMode === "range" && (
                    <span className="text-text-muted">
                        {" "}The sensitivity range never touches this — it's the
                        physical driver.
                    </span>
                )}
            </p>

            {/* Provenance */}
            {breakdown && Object.keys(breakdown).length > 0 ? (
                <div className="mb-6">
                    <div className="mb-2 text-caption uppercase tracking-wide text-text-muted">
                        Profile provenance
                        {typeof hours === "number" && (
                            <span className="ml-1 normal-case tracking-normal text-text-muted">
                                ({hours.toLocaleString()} h)
                            </span>
                        )}
                    </div>
                    <ProvenanceBar breakdown={breakdown} />
                </div>
            ) : (
                <p className="mb-6 text-body-sm text-text-muted">
                    Provenance breakdown not available for this run.
                </p>
            )}

            {/* Daily generation shape */}
            <div className="mb-2 text-caption uppercase tracking-wide text-text-muted">
                Daily generation
            </div>
            {isLoading ? (
                <div className="flex h-[220px] items-center justify-center text-body-sm text-text-muted">
                    Loading generation shape…
                </div>
            ) : chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                        <defs>
                            <linearGradient id="genFill" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#34d399" stopOpacity={0.45} />
                                <stop offset="100%" stopColor="#34d399" stopOpacity={0.02} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#2a2f3a" vertical={false} />
                        <XAxis
                            dataKey="date"
                            tick={{ fontSize: 10, fill: "#9ca3af" }}
                            tickFormatter={(d: string) => (d ? d.slice(0, 7) : "")}
                            minTickGap={40}
                        />
                        <YAxis
                            tick={{ fontSize: 10, fill: "#9ca3af" }}
                            width={48}
                            tickFormatter={(v: number) => `${Math.round(v)}`}
                        />
                        <Tooltip content={<GenTooltip />} />
                        <Area
                            type="monotone"
                            dataKey="mwh"
                            stroke="#34d399"
                            strokeWidth={1.5}
                            fill="url(#genFill)"
                        />
                    </AreaChart>
                </ResponsiveContainer>
            ) : (
                <div className="flex h-[220px] items-center justify-center text-body-sm text-text-muted">
                    No daily generation data.
                </div>
            )}
        </div>
    );
}

export default GenerationProfileCard;
